'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Card,
  CardContent,
  SelectChangeEvent
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';

// 用户接口定义
interface User {
  id: number;
  username: string;
  display_name?: string;
  email?: string;
  role: string;
  organization_name?: string;
  organization_id?: number;
  org_id?: number;
  project_count?: number;
  created_at: string;
}

// 组织接口定义
interface Organization {
  id: number;
  legal_name: string;
}

// 套餐接口定义
interface Plan {
  id: number;
  name: string;
  description?: string;
  price_yuan: number;
  price_cents: number;
  duration_value: number;
  duration_unit: string;
  project_credits_total: number;
  max_users: number;
  status: string;
  is_trial: boolean;
}

// 表单数据接口
interface FormData {
  username: string;
  display_name: string;
  email: string;
  password: string;
  role: string;
  organization_name: string;
  organization_id: string;
}

// 获取当前登录用户的信息
const getCurrentUser = (): User | null => {
  try {
    const userStr = localStorage.getItem('currentUser');
    return userStr ? JSON.parse(userStr) : null;
  } catch (e) {
    console.error("Error parsing current user from localStorage:", e);
    return null;
  }
};

// 获取认证token
const getAuthToken = (): string | null => {
  try {
    return localStorage.getItem('auth_token');
  } catch (e) {
    console.error("Error getting auth token:", e);
    return null;
  }
};

// 创建带认证头的fetch请求
const fetchWithAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    ...options,
    headers,
  });
};

// 角色显示转换函数
const getRoleDisplayName = (role: string): string => {
  const roleMap: Record<string, string> = {
    'super_admin': '超级管理员',
    'zhaoyuan_admin': '昭元管理员',
    'admin': '管理员',
    'writer': '写作员'
  };
  return roleMap[role] || role;
};

// 时长格式化函数
const formatDurationLabel = (durationValue: number, durationUnit: string): string => {
  if (!durationValue || !durationUnit) return '';
  
  switch (durationUnit) {
    case 'day':
      return `${durationValue}天`;
    case 'week':
      return `${durationValue}周`;
    case 'month':
      return `${durationValue}个月`;
    case 'year':
      return `${durationValue}年`;
    default:
      return `${durationValue}${durationUnit}`;
  }
};

export default function UserManagement(): JSX.Element {
  console.log('UserManagement component rendering...');
  
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false); // 先设为false，避免初始加载
  const [error, setError] = useState<string | null>(null);
  const [openFormDialog, setOpenFormDialog] = useState<boolean>(false);
  const [currentUserForEdit, setCurrentUserForEdit] = useState<User | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  // 删除用户确认对话框状态
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // 开通会员相关状态
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState<boolean>(false);
  const [selectedUserForSubscription, setSelectedUserForSubscription] = useState<User | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [plansLoading, setPlansLoading] = useState<boolean>(false);
  const [subscriptionError, setSubscriptionError] = useState<string>('');
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  const [formData, setFormData] = useState<FormData>({
    username: '',
    display_name: '',
    email: '',
    password: '',
    role: 'writer',
    organization_name: '',
    organization_id: '',
  });

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentUserInfo, setCurrentUserInfo] = useState<User | null>(null);
  
  // 使用useMemo缓存loggedInUser，避免无限循环
  const loggedInUser = useMemo(() => getCurrentUser(), []);
  
  useEffect(() => {
    const fetchCurrentUserInfo = async () => {
      if (loggedInUser && (!loggedInUser.organization_name || !loggedInUser.organization_id)) {
        try {
          const response = await fetchWithAuth('/api/v1/users/me');
          if (response.ok) {
            const userInfo = await response.json();
            setCurrentUserInfo(userInfo);
            // 更新localStorage中的用户信息
            const updatedUser = {
              ...loggedInUser,
              organization_name: userInfo.organization_name,
              organization_id: userInfo.organization_id
            };
            localStorage.setItem('currentUser', JSON.stringify(updatedUser));
          }
        } catch (error) {
          console.error('获取用户信息失败:', error);
        }
      } else {
        setCurrentUserInfo(loggedInUser);
      }
    };
    
    fetchCurrentUserInfo();
  }, [loggedInUser]);
  
  // 使用currentUserInfo或loggedInUser
  const effectiveUser = currentUserInfo || loggedInUser;
  
  // admin、zhaoyuan_admin 和 super_admin 可以管理用户
  const canManageUsers = effectiveUser && (effectiveUser.role === 'admin' || effectiveUser.role === 'zhaoyuan_admin' || effectiveUser.role === 'super_admin');
  // 只有 super_admin 和 zhaoyuan_admin 可以删除用户
  const canDeleteUsers = effectiveUser && (effectiveUser.role === 'super_admin' || effectiveUser.role === 'zhaoyuan_admin');
  // 只有 super_admin 可以修改用户角色
  const canModifyRoles = effectiveUser && effectiveUser.role === 'super_admin';

  const fetchOrganizations = useCallback(async () => {
    if (loggedInUser?.role !== 'super_admin') {
      return;
    }
    try {
      const response = await fetchWithAuth('/api/v1/organizations/');
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || '获取公司列表失败');
      }
      const data = await response.json();
      setOrganizations(data.data || []);
    } catch (err: any) {
      console.error('Fetch organizations error:', err);
      setError(err.message);
    }
  }, [loggedInUser?.role]);

  const fetchUsers = useCallback(async () => {
    if (!canManageUsers) {
        setError('您没有权限查看此页面。');
        setLoading(false);
        setUsers([]);
        return;
    }
    setLoading(true);
    try {
      // 先尝试获取第一页，看看API是否正常工作
      console.log('UserManagement - Starting to fetch users...');
      const response = await fetchWithAuth('/api/v1/users/?page=1&page_size=100');
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || '获取用户列表失败');
      }
      
      const data = await response.json();
      console.log('UserManagement - First page response:', data);
      
      // 简化处理：先只显示第一页的数据
      setUsers(data.data || []);
      setError(null);
      
    } catch (err: any) {
      console.error('Fetch users error:', err);
      setError(err.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [canManageUsers]);

  useEffect(() => {
    console.log('UserManagement useEffect triggered');
    fetchUsers();
    fetchOrganizations();
  }, [fetchUsers, fetchOrganizations]);

  const checkUserLimit = async (): Promise<{ canCreate: boolean; message: string | null; maxUsers?: number; currentCount?: number }> => {
    try {
      const response = await fetchWithAuth('/api/v1/users/check-limit');
      const data = await response.json();
      
      if (!response.ok) {
        if (data.error && data.error.includes('用户数量已达上限')) {
          return { canCreate: false, message: data.error };
        }
        return { canCreate: false, message: data.error || '检查用户数量限制失败' };
      }
      
      return { 
        canCreate: data.data?.canCreate || false, 
        message: null,
        maxUsers: data.data?.maxUsers,
        currentCount: data.data?.currentCount
      };
    } catch (error) {
      console.error('检查用户数量限制失败:', error);
      return { canCreate: false, message: '检查用户数量限制失败' };
    }
  };

  const handleOpenDialog = async (user: User | null = null) => {
    if (user) {
      // 编辑用户时不需要检查限制
      console.log('=== 编辑用户调试信息 ===');
      console.log('用户数据:', user);
      console.log('org_id:', user.org_id);
      console.log('organization_id:', user.organization_id);
      console.log('organization_name:', user.organization_name);
      
      setIsEditMode(true);
      setCurrentUserForEdit(user);
      setFormData({
        username: user.username,
        display_name: user.display_name || '',
        email: user.email || '',
        role: user.role,
        password: '', // 编辑时不显示密码，但可以设置新密码
        organization_name: user.organization_name || '',
        organization_id: String(user.org_id || user.organization_id || '')
      });
      
      console.log('设置的表单数据:', {
        username: user.username,
        display_name: user.display_name || '',
        email: user.email || '',
        role: user.role,
        password: '',
        organization_name: user.organization_name || '',
        organization_id: user.organization_id || ''
      });
      
      setError(null);
      setOpenFormDialog(true);
    } else {
      // 添加新用户时先检查用户数量限制
      console.log('开始检查用户数量限制...');
      const limitCheck = await checkUserLimit();
      console.log('用户数量限制检查结果:', limitCheck);
      
      if (!limitCheck.canCreate) {
        console.log('用户数量已达上限，阻止弹窗打开');
        const errorMessage = limitCheck.message || `用户数量已达上限，当前套餐最多支持 ${limitCheck.maxUsers || 0} 个用户`;
        setError(errorMessage);
        return; // 不打开弹窗
      }
      
      console.log('用户数量检查通过，打开弹窗');
      setIsEditMode(false);
      setCurrentUserForEdit(null);
      // 添加新用户时，公司名称显示当前用户所属的公司
      setFormData({
        username: '',
        display_name: '',
        email: '',
        password: '',
        role: 'writer',
        organization_name: effectiveUser?.organization_name || '',
        organization_id: String(effectiveUser?.organization_id || '')
      });
      setError(null);
      setOpenFormDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenFormDialog(false);
    setCurrentUserForEdit(null);
    setIsEditMode(false);
    // 重置表单数据
    setFormData({
      username: '',
      display_name: '',
      email: '',
      password: '',
      role: 'writer',
      organization_name: '',
      organization_id: '',
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name as string]: value }));
  };

  const handleSubmit = async () => {
    setError(null);
    let url = '/api/v1/users/';
    let method = 'POST';

    const payload = { ...formData };
    
    // 处理公司信息：使用当前用户所属的公司ID
    if (payload.organization_id) {
      (payload as any).organization_id = parseInt(payload.organization_id);
      delete (payload as any).organization_name; // 删除名称字段，只使用ID
    }
    
    if (isEditMode && currentUserForEdit) {
      url = `/api/v1/users/${currentUserForEdit.id}`;
      method = 'PUT';
      // 编辑时，如果密码字段为空，则不发送密码，避免意外清空或修改密码
      if (!payload.password) {
        delete (payload as any).password;
      }
    } else {
        // 创建用户时，根据用户名生成默认密码和邮箱
        if (!payload.username) {
            setError("创建用户时手机号不能为空。");
            return;
        }
        
        // 验证手机号格式
        if (!/^1[3-9]\d{9}$/.test(payload.username)) {
            setError("请输入正确的11位手机号。");
            return;
        }
        
        // 如果密码为空，生成默认密码：手机号后六位
        if (!payload.password) {
          const defaultPassword = payload.username.slice(-6);
          payload.password = defaultPassword;
        }
        
        // 不再自动生成默认邮箱
    }

    try {
      const response = await fetchWithAuth(url, {
        method: method,
        body: JSON.stringify(payload),
      });
      const responseData = await response.json();
      if (!response.ok) {
        // 优先提取后端返回的详细错误信息
        const errorMessage = responseData.detail || 
                            responseData.message || 
                            responseData.error || 
                            (isEditMode ? '更新用户失败' : '创建用户失败');
        throw new Error(errorMessage);
      }
      fetchUsers(); // 重新加载用户列表
      handleCloseDialog();
    } catch (err: any) {
      console.error('Submit user error:', err);
      // 确保错误信息正确显示
      const errorMessage = err.message || (isEditMode ? '更新用户失败' : '创建用户失败');
      setError(errorMessage);
    }
  };

  const handleDelete = async (userId: number) => {
    // 找到要删除的用户信息
    const user = users.find(u => u.id === userId);
    setUserToDelete(user || null);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    setError(null);
    try {
        const response = await fetchWithAuth(`/api/v1/users/${userToDelete.id}`, {
            method: 'DELETE'
        });
        const responseData = await response.json();
        if (!response.ok) {
            throw new Error(responseData.error || '删除用户失败');
        }
        fetchUsers(); // Refresh list
        setDeleteConfirmOpen(false);
        setUserToDelete(null);
    } catch (err: any) {
        console.error('Delete user error:', err);
        setError(err.message);
    }
  };

  // 开通会员相关函数
  const handleOpenSubscriptionDialog = async (user: User) => {
    setSelectedUserForSubscription(user);
    setSubscriptionError('');
    setPlansLoading(true);
    
    try {
      // 使用与支付对话框相同的套餐获取逻辑
      const res = await fetchWithAuth('/api/v1/plans/');
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || '加载套餐失败');
      
      const rows = (data.data || []).filter((p: Plan) => {
        const isActive = p.status === 'active';
        const sellable = !p.is_trial; // 非试用
        const hasPrice = (Number(p.price_yuan) > 0) || (Number(p.price_cents) > 0);
        return isActive && sellable && hasPrice;
      });
      
      // 按价格从低到高排序
      rows.sort((a: Plan, b: Plan) => (a.price_yuan || 0) - (b.price_yuan || 0));
      
      setPlans(rows);
      setSubscriptionDialogOpen(true);
    } catch (err: any) {
      console.error('获取套餐列表失败:', err);
      setSubscriptionError('获取套餐列表失败');
    } finally {
      setPlansLoading(false);
    }
  };

  const handleCloseSubscriptionDialog = () => {
    setSubscriptionDialogOpen(false);
    setSelectedUserForSubscription(null);
    setSubscriptionError('');
    setSelectedPlan(null);
  };

  // 选择套餐
  const handlePlanSelect = (plan: Plan) => {
    setSelectedPlan(plan);
  };

  const handleCreateSubscription = async (planId: number) => {
    if (!selectedUserForSubscription) return;
    
    setPlansLoading(true);
    setSubscriptionError('');
    
    try {
      const response = await fetchWithAuth('/api/v1/admin/create-subscription', {
        method: 'POST',
        body: JSON.stringify({
          user_id: selectedUserForSubscription.id,
          plan_id: planId
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubscriptionDialogOpen(false);
        setSelectedUserForSubscription(null);
        // 刷新用户列表
        fetchUsers();
        setError(null);
      } else {
        setSubscriptionError(data.error || '开通会员失败');
      }
    } catch (err: any) {
      console.error('开通会员失败:', err);
      setSubscriptionError('开通会员失败');
    } finally {
      setPlansLoading(false);
    }
  };

  console.log('UserManagement - canManageUsers:', canManageUsers, 'loading:', loading, 'users.length:', users.length, 'error:', error);
  console.log('UserManagement - effectiveUser:', effectiveUser);
  console.log('UserManagement - loggedInUser:', loggedInUser);
  
  // 如果正在加载，显示加载状态
  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>加载用户数据中...</Typography>
        </Box>
      </Paper>
    );
  }
  
  if (!canManageUsers) {
    return (
        <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
            <Alert severity="error">{error || '您没有权限访问此页面。'}</Alert>
        </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" component="h2">
          用户管理
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleOutlineIcon />}
          onClick={async () => await handleOpenDialog()}
        >
          添加用户
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : users.length === 0 && !error ? (
        <Alert severity="info">当前没有用户。</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>序号</TableCell>
                <TableCell>用户名</TableCell>
                <TableCell>显示名称</TableCell>
                <TableCell>公司</TableCell>
                <TableCell>邮箱</TableCell>
                <TableCell>角色</TableCell>
                <TableCell>项目数量</TableCell>
                <TableCell>创建时间</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users
                .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) // 按创建时间升序排序
                .map((user, index) => (
                <TableRow key={user.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.display_name}</TableCell>
                  <TableCell>{user.organization_name || '未分配'}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{getRoleDisplayName(user.role)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={`${user.project_count || 0}个`} 
                      size="small" 
                      color={user.project_count && user.project_count > 0 ? 'primary' : 'default'}
                      variant={user.project_count && user.project_count > 0 ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>{new Date(new Date(user.created_at).getTime() + 8 * 60 * 60 * 1000).toLocaleString('zh-CN')}</TableCell>
                  <TableCell>
                    <IconButton onClick={async () => await handleOpenDialog(user)} size="small">
                      <EditIcon />
                    </IconButton>
                    {canDeleteUsers && (
                      <IconButton onClick={() => handleDelete(user.id)} size="small" disabled={loggedInUser ? loggedInUser.id === user.id : false}>
                         {/* 不能删除自己 */} 
                        <DeleteIcon />
                      </IconButton>
                    )}
                    {loggedInUser && loggedInUser.role === 'super_admin' && (
                      <IconButton 
                        onClick={() => handleOpenSubscriptionDialog(user)} 
                        size="small"
                        title="开通会员"
                      >
                        <StarIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 用户创建/编辑对话框 */}
      <Dialog open={openFormDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{isEditMode ? '编辑用户' : '添加新用户'}</DialogTitle>
        <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {!isEditMode && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  提示：如果密码留空，系统将自动生成：
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • 默认密码：手机号后六位
                </Typography>
              </Alert>
            )}
            <TextField
                autoFocus
                margin="dense"
                name="username"
                label="手机号"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.username}
                onChange={handleChange}
                required
                disabled={isEditMode} // 用户名通常不允许修改
                placeholder="请输入11位手机号"
                error={formData.username ? !/^1[3-9]\d{9}$/.test(formData.username) : false}
                helperText={formData.username && !/^1[3-9]\d{9}$/.test(formData.username) ? "请输入正确的11位手机号" : ""}
                sx={{ mb: 2 }}
            />
            <TextField
                margin="dense"
                name="display_name"
                label="显示名称"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.display_name}
                onChange={handleChange}
                sx={{ mb: 2 }}
            />
            {loggedInUser?.role === 'super_admin' ? (
              <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                <InputLabel id="organization-select-label">选择公司</InputLabel>
                <Select
                  labelId="organization-select-label"
                  name="organization_id"
                  value={String(formData.organization_id || '')}
                  label="选择公司"
                  onChange={handleChange}
                  required
                >
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={String(org.id)}>
                      {org.legal_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                margin="dense"
                name="organization_name"
                label="公司名称"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.organization_name}
                disabled={true}
                sx={{ mb: 2 }}
              />
            )}
            <TextField
                margin="dense"
                name="email"
                label="邮箱地址"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.email}
                onChange={handleChange}
                placeholder=""
                sx={{ mb: 2 }}
            />
            <TextField
                margin="dense"
                name="password"
                label={isEditMode ? "新密码 (留空则不修改)" : "密码"}
                type="password"
                fullWidth
                variant="outlined"
                value={formData.password}
                onChange={handleChange}
                required={!isEditMode}
                placeholder={!isEditMode ? "留空则默认为用户名后六位" : ""}
                sx={{ mb: 2 }}
            />
            <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                <InputLabel id="role-select-label">角色</InputLabel>
                <Select
                    labelId="role-select-label"
                    name="role"
                    value={formData.role}
                    label="角色"
                    onChange={handleChange}
                    disabled={!isEditMode || !canModifyRoles} // 创建时或非超级管理员时禁用角色选择
                >
                    <MenuItem value="super_admin">超级管理员 (super_admin)</MenuItem>
                    <MenuItem value="zhaoyuan_admin">昭元管理员 (zhaoyuan_admin)</MenuItem>
                    <MenuItem value="admin">管理员 (admin)</MenuItem>
                    <MenuItem value="writer">写作员 (writer)</MenuItem>
                </Select>
            </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button onClick={handleSubmit} variant="contained">
            {isEditMode ? '保存更改' : '创建用户'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 删除用户确认对话框 */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-user-dialog-title"
        aria-describedby="delete-user-dialog-description"
      >
        <DialogTitle id="delete-user-dialog-title">
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">确认删除用户</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-user-dialog-description">
            <Typography variant="body1" gutterBottom>
              确定要删除此用户吗？此操作不可恢复。
            </Typography>
            {userToDelete && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                用户名：<strong>{userToDelete.username}</strong>
                {userToDelete.display_name && (
                  <span> | 显示名称：<strong>{userToDelete.display_name}</strong></span>
                )}
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            取消
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            确认删除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 开通会员对话框 */}
      <Dialog open={subscriptionDialogOpen} onClose={handleCloseSubscriptionDialog} fullWidth maxWidth="md">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon color="primary" />
            <Typography variant="h6" component="span">
              为 {selectedUserForSubscription?.display_name} 开通会员
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {subscriptionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {subscriptionError}
            </Alert>
          )}
          
          {plansLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              {Array.isArray(plans) && plans.length > 0 ? plans.map((plan) => (
                <Card
                  key={plan.id}
                  onClick={() => handlePlanSelect(plan)}
                  sx={{
                    cursor: 'pointer',
                    border: selectedPlan?.id === plan.id ? 2 : 1,
                    borderColor: selectedPlan?.id === plan.id ? 'primary.main' : 'divider',
                    transition: 'all 0.2s ease-in-out',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          {plan.name}
                        </Typography>
                        {plan.description && (
                          <Typography variant="body2" color="text.secondary">
                            {plan.description}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h4" color="primary" fontWeight="bold">
                          ¥{Number(plan.price_yuan || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDurationLabel(plan.duration_value, plan.duration_unit)}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                        项目数量：{plan.project_credits_total > 10000 ? '不限制' : `${plan.project_credits_total}个`}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                        账号数量：{plan.max_users && plan.max_users > 10000 ? '不限制' : `${plan.max_users || 1}个`}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )) : (
                <Alert severity="info">
                  暂无可用的套餐
                </Alert>
              )}
            </Box>
          )}

          {/* 说明信息 */}
          <Alert severity="info">
            开通后将为该用户所在公司创建订单记录，支付方式记录为对公转账。
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubscriptionDialog} size="large">
            取消
          </Button>
          {selectedPlan && (
            <Button
              variant="contained"
              size="large"
              onClick={() => handleCreateSubscription(selectedPlan.id)}
              disabled={plansLoading}
              startIcon={<StarIcon />}
            >
              {plansLoading ? '开通中...' : `为 ${selectedUserForSubscription?.display_name} 开通 ${selectedPlan.name}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
